import { useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchLive, fetchPrematch, fetchPrematchV2, fetchOverview, fetchTodayPerformance, fetchFeed, fetchActiveSignals, fetchLiveDetail,
  fetchLiveNow, fetchLiveCenter,
} from "@/api/endpoints";
import { fetchMatchEnrichment } from "@/api/enrichment";
import { fetchMarketHub } from "@/api/marketHub";
import { useSession } from "@/context/SessionContext";
import { useLaunch } from "@/context/LaunchContext";
import { fixtureKeys } from "@/presentation/fixtureIdentity";
import { LiveNowFixture, LiveCenterFixture } from "@/types/api";
import { MarketFixture } from "@/types/marketHub";

const LIVE_REFETCH = 15_000;
const STALE = 10_000;

export function useLive(limit = 80) {
  return useQuery({ queryKey: ["live", limit], queryFn: () => fetchLive(limit), refetchInterval: LIVE_REFETCH, staleTime: STALE });
}
export function useActiveSignals(limit = 12) {
  return useQuery({ queryKey: ["active", limit], queryFn: () => fetchActiveSignals(limit), refetchInterval: LIVE_REFETCH, staleTime: STALE });
}
export function usePrematch(limit = 60, authed = false) {
  return useQuery({
    queryKey: ["prematch", limit, authed],
    queryFn: async () => {
      if (authed) {
        try { return await fetchPrematchV2(limit); }
        catch { return await fetchPrematch(limit); } // safe fallback to public
      }
      return fetchPrematch(limit);
    },
    refetchInterval: 60_000,
    staleTime: STALE,
  });
}
export function useOverview() {
  return useQuery({ queryKey: ["overview"], queryFn: fetchOverview, refetchInterval: 30_000, staleTime: STALE });
}
export function useTodayPerformance() {
  return useQuery({ queryKey: ["today-perf"], queryFn: fetchTodayPerformance, refetchInterval: 60_000, staleTime: STALE });
}
export function useFeed(limit = 12) {
  return useQuery({ queryKey: ["feed", limit], queryFn: () => fetchFeed(limit), refetchInterval: 30_000, staleTime: STALE });
}
export function useMatchEnrichment(fixtureId: string | undefined) {
  return useQuery({
    queryKey: ["enrich", fixtureId],
    queryFn: () => fetchMatchEnrichment(fixtureId as string),
    enabled: !!fixtureId,
    staleTime: 120_000,
    retry: false,
  });
}
// Rich per-decision ATLAS detail. Refetches with LIVE cadence so the reasoning
// and replay evolve on real backend updates (never interpolated/fabricated).
export function useLiveDetail(decisionId: string | number | undefined) {
  const id = decisionId != null ? String(decisionId) : undefined;
  return useQuery({
    queryKey: ["live-detail", id],
    queryFn: () => fetchLiveDetail(id as string),
    enabled: !!id,
    refetchInterval: LIVE_REFETCH,
    staleTime: STALE,
    retry: false,
  });
}

// Authoritative realtime LIVE NOW (authenticated global feed). Only enabled for
// real sessions; preview/unauth sessions fall back to ATLAS-signal-derived live.
export function useLiveNow(enabled: boolean) {
  return useQuery({
    queryKey: ["live-now"],
    queryFn: fetchLiveNow,
    enabled,
    refetchInterval: 15_000,
    staleTime: 10_000,
    retry: false,
  });
}

// Per-fixture Live Center detail (authenticated). fixture_id preserved. Polls at
// LIVE cadence while the match is in play; stops once it is finished/resolved so
// completed matches are not polled indefinitely.
export function useLiveCenter(fixtureId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["live-center", fixtureId],
    queryFn: () => fetchLiveCenter(fixtureId as string),
    enabled: enabled && !!fixtureId,
    refetchInterval: (query) => {
      const data = query.state.data as any;
      if (!data) return 15_000;
      const phase = String(data.status ?? data.lifecycle_state ?? "");
      const finished = !!data.resolution || /\b(FT|AET|PEN|FINISHED|ENDED|RESOLVED)\b/i.test(phase);
      return finished ? false : 15_000;
    },
    staleTime: 10_000,
    retry: false,
  });
}

// Canonical Market Hub product model (authoritative backend evaluation),
// additive. Deduped single query; joined to live/prematch items by fixture_id.
export function useMarketHub() {
  const { isAuthenticated } = useSession();
  return useQuery({
    queryKey: ["market-hub-v1"],
    queryFn: fetchMarketHub,
    enabled: isAuthenticated,
    staleTime: 20_000,
    refetchInterval: 30_000,
    retry: 1,
  });
}

// Indexed Market Hub view: every canonical fixture key -> MarketFixture, so any
// live/prematch item can look up its authoritative recommendations by id.
export function useMarketHubMap() {
  const q = useMarketHub();
  const map = useMemo(() => {
    const m = new Map<string, MarketFixture>();
    for (const f of q.data?.fixtures || []) {
      const keys = f.keys && f.keys.length ? f.keys : [f.fixture_id];
      for (const k of keys) if (!m.has(k)) m.set(k, f);
    }
    return m;
  }, [q.data]);
  const lookup = useCallback((ref: unknown): MarketFixture | null => {
    for (const k of fixtureKeys(ref)) { const hit = map.get(k); if (hit) return hit; }
    return null;
  }, [map]);
  return { view: q.data ?? null, map, lookup, isLoading: q.isLoading };
}

// Authoritative CURRENT live state for a single fixture. The public /live feed
// (LiveSignal) carries only the signal SNAPSHOT (minute / initial_* ) and no
// crests. The authenticated live-now feed (/data/live?live=all) carries the real
// current minute/score/status + team & league crests. This joins them by
// CANONICAL fixture identity (any of fixture/match/provider id, string-normalized)
// so a fixture keyed under an alternate id still merges — no fuzzy name matching.
// Deduped: all callers share the single ["live-now"] query. Null for
// unauthenticated/preview sessions or when the fixture is not in the live feed.
export function useLiveOverlay(fixtureRef: string | Record<string, any> | undefined): LiveNowFixture | LiveCenterFixture | null {
  const { isAuthenticated } = useSession();
  // LIVE is a backend entitlement (app:live:full → PRO+). Never request the
  // protected live-now / live-center sources for plans without it (FREE),
  // avoiding 403s and keeping LIVE strictly gated.
  const canLive = useLaunch().hasPlan("PRO");
  const q = useLiveNow(isAuthenticated && canLive);
  const wanted = fixtureRef == null ? [] : fixtureKeys(fixtureRef);
  const keyStr = wanted.join("|");
  const fromBatch = useMemo(() => {
    if (!wanted.length || !Array.isArray(q.data)) return null;
    const wset = new Set(wanted);
    return (q.data as LiveNowFixture[]).find((f) => {
      const fk = f.keys && f.keys.length ? f.keys : [f.fixture_id];
      return fk.some((k) => wset.has(k));
    }) ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, keyStr]);
  // Authoritative per-fixture fallback for fixtures the global live feed no longer
  // lists (e.g. at/after full time) — the SAME source MatchDetail consumes
  // (/atlas/data/live-center/{fixture_id}). Enabled ONLY when the batch misses,
  // so in-play cards make zero extra requests and finished ones still show the
  // real current/near-final state instead of the stale signal snapshot.
  const fallbackId = wanted.length ? wanted[0] : undefined;
  const lc = useLiveCenter(fromBatch ? undefined : fallbackId, isAuthenticated && canLive && !fromBatch && !!fallbackId);
  return fromBatch ?? (lc.data as LiveCenterFixture | null) ?? null;
}
